import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Container,
    Typography,
    Radio,
    RadioGroup,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { riskProfileMasterData } from '../../../redux-store/actions';
import { useLocation, useHistory } from 'react-router';

interface Value {
    key: string;
    label: string;
    score: number;
}

interface Question {
    questionType: 'radio' | 'multiSelectCheckBox';
    key: string;
    displayText: string;
    required: string;
    isMultiselect: string;
    values: Value[];
}

interface Profile {
    profile: string;
    minScore: number;
    maxScore: number;
}

interface RiskProfileData {
    riskProfile: Question[];
    profile: Profile[];
}


const RiskProfilePage: React.FC = () => {
    const dispatch = useDispatch();
    const [riskProfileData, setRiskProfileData] = useState<RiskProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const history = useHistory()
    const [isOptedOut, setIsOptedOut] = useState(false);
    const [answers, setAnswers] = useState<{ [key: string]: string | string[] }>({});
    const [isDeclared, setIsDeclared] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const location = useLocation<any>();
    const UserData = location.state?.UserData;
    console.log(UserData)

    const LOCAL_STORAGE_KEY = 'riskProfileProgress';

    useEffect(() => {
        const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedData) {
            const { savedAnswers, savedIsOptedOut, savedIsDeclared } = JSON.parse(savedData);
            setAnswers(savedAnswers || {});
            setIsOptedOut(savedIsOptedOut || false);
            setIsDeclared(savedIsDeclared || false);
        }
    }, []);

    useEffect(() => {
        if (!loading) {
            const dataToSave = {
                savedAnswers: answers,
                savedIsOptedOut: isOptedOut,
                savedIsDeclared: isDeclared,
            };
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
        }
    }, [answers, isOptedOut, isDeclared, loading]);

    useEffect(() => {
        const fetchData = async () => {
            try {

                const response = (await dispatch(riskProfileMasterData())) as unknown as any;
                setRiskProfileData(response);
            } catch (err) {
                setError("Failed to retrieve risk profile data.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dispatch]);

    const handleRadioChange = (questionKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setAnswers((prev) => ({
            ...prev,
            [questionKey]: e.target.value,
        }));
    };

    const allQuestionsAnswered = useMemo(() => {
        // If the user opts out or data hasn't loaded, we don't need to check.
        if (isOptedOut || !riskProfileData) return true;

        // Returns true only if the count of answers matches the total question count.
        return riskProfileData.riskProfile.length === Object.keys(answers).length;
    }, [answers, riskProfileData, isOptedOut]);

    const handleNext = () => {
        setAlertMessage(null);

        if (!isOptedOut && !allQuestionsAnswered) {
            setAlertMessage('Please answer all required questions before proceeding.');
            return;
        }

        const riskProfilePayload = {
            isOptedOut: isOptedOut,
            riskProfileAssessment: isOptedOut ? null : {
                questionsAndAnswers: riskProfileData?.riskProfile.map(question => {
                    const answerKeys = answers[question.key];
                    let selectedAnswers: string[] = [];

                    if (answerKeys) {
                        const answerKeysArray = Array.isArray(answerKeys) ? answerKeys : [answerKeys];
                        selectedAnswers = answerKeysArray.map(key => {
                            return question.values.find(v => v.key === key)?.label || 'N/A';
                        });
                    }

                    return {
                        question: question.displayText,
                        answers: selectedAnswers,
                    };
                }),
                totalScore: totalScore,
                determinedProfile: riskProfile,
            },
        };

        history.push("/investor-risk-preference", { UserData, riskProfilePayload, riskProfileData });
    };

    const handleCheckboxChange = (questionKey: string, valueKey: string, isChecked: boolean) => {
        setAnswers((prev) => {
            const currentAnswers = (prev[questionKey] as string[] | undefined) || [];
            let newAnswers: string[];

            if (isChecked) {
                newAnswers = [...currentAnswers, valueKey];
            } else {
                newAnswers = currentAnswers.filter((key) => key !== valueKey);
            }

            return { ...prev, [questionKey]: newAnswers };
        });
    };

    const totalScore = useMemo(() => {
        if (!riskProfileData) return 0;
        let score = 0;
        for (const questionKey in answers) {
            const question = riskProfileData.riskProfile.find(q => q.key === questionKey);
            if (question) {
                const selectedAnswer = answers[questionKey];
                if (Array.isArray(selectedAnswer)) {
                    selectedAnswer.forEach(answerKey => {
                        const value = question.values.find(v => v.key === answerKey);
                        if (value) score += value.score;
                    });
                } else {
                    const value = question.values.find(v => v.key === selectedAnswer);
                    if (value) score += value.score;
                }
            }
        }
        return score;
    }, [answers, riskProfileData]);

    const riskProfile = useMemo(() => {
        if (!riskProfileData || totalScore === 0) return 'Not yet determined';
        const profile = riskProfileData.profile.find(
            p => totalScore >= p.minScore && totalScore <= p.maxScore
        );
        return profile ? profile.profile : 'Unable to determine profile';
    }, [totalScore, riskProfileData]);

    if (loading) {
        return (
            <Box sx={{ width: '100%', mt: 4 }}>
                <LinearProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Box sx={{ width: '100%', backgroundColor: '#f8fbfd', py: 4 }}>
            <Container maxWidth="md" sx={{ my: 4 }}>
                <Paper sx={{ p: 4, boxShadow: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
                        Risk Profile
                    </Typography>

                    <FormControlLabel
                        control={<Checkbox checked={isOptedOut} onChange={(e) => setIsOptedOut(e.target.checked)} />}
                        label="I do not wish to undergo the RISK Profiling and want to use the platform of products and services as an execution-only platform, whereby all investment decisions would be my sole discretion and decision"
                        sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}
                    />

                    <Box sx={{ pointerEvents: isOptedOut ? 'none' : 'auto', opacity: isOptedOut ? 0.5 : 1 }}>
                        {riskProfileData?.riskProfile.map((question, index) => {
                            const isMulti = question.isMultiselect === 'true';
                            return (
                                <Accordion key={question.key} sx={{ mb: 1 }}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`panel${index}a-content`}
                                        id={`panel${index}a-header`}
                                        sx={{ backgroundColor: '#f8fbfd' }}
                                    >
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                            {index + 1}. {question.displayText}
                                            {!isOptedOut && <span style={{ color: 'red' }}> *</span>}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ backgroundColor: '#f8fbfd' }}>
                                        {isMulti ? (
                                            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                                                <FormGroup>
                                                    {question.values.map((value) => (
                                                        <FormControlLabel
                                                            key={value.key}
                                                            control={
                                                                <Checkbox
                                                                    checked={((answers[question.key] as string[]) || []).includes(value.key)}
                                                                    onChange={(e) => handleCheckboxChange(question.key, value.key, e.target.checked)}
                                                                />
                                                            }
                                                            label={value.label}
                                                            sx={{ backgroundColor: '#f8fbfd', m: 0.5, borderRadius: 1 }}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </FormControl>
                                        ) : (
                                            <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                                                <RadioGroup
                                                    value={answers[question.key] || ''}
                                                    onChange={(e) => handleRadioChange(question.key, e)}
                                                >
                                                    {question.values.map((value) => (
                                                        <FormControlLabel
                                                            key={value.key}
                                                            value={value.key}
                                                            control={<Radio />}
                                                            label={value.label}
                                                            sx={{ backgroundColor: '#f8fbfd', m: 0.5, borderRadius: 1 }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </FormControl>
                                        )}
                                    </AccordionDetails>

                                </Accordion>
                            );
                        })}
                    </Box>

                    {!isOptedOut && Object.keys(answers).length > 0 && (
                        <Box sx={{ p: 2, mt: 3, backgroundColor: 'primary.light', color: 'primary.contrastText', boxShadow: 'none' }}>
                            <Typography>Your total Score is <strong>{totalScore}</strong></Typography>
                            <Typography>Risk Profile is <strong>{riskProfile}</strong></Typography>
                        </Box>
                    )}

                    {alertMessage && (
                        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setAlertMessage(null)}>
                            {alertMessage}
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button variant="contained" color="primary" onClick={handleNext}>
                            Next
                        </Button>
                    </Box>

                </Paper>
            </Container>
        </Box>
    );
};

export default RiskProfilePage;